<!-- This component is for a membership when displayed in a list, e.g. on the Manage Account page. -->

<template>
    <tr :class="{'selected': isSelected, 'inactive': !isActive}"
        class="membership-list-membership dt-table__row--clickable"
        @click="$emit('click')">
        <!-- Membership ref cell (mobile only - becomes the header on mobile) -->
        <td class="mobile-only">
            {{ membership.ref }}

            <div v-if="isPostponed"
                 class="dt-table__cell-subtitle">
                ({{ l('VALID_FROM') }} {{ dateFilter(membership.getInitialStartDate()) }})
            </div>
        </td>

        <!-- Expires auto-renew cell + ref on desktop-->
        <td>
            <div class="non-mobile-only">
                <i class="fa-solid fa-fw fa-barcode"></i>
                {{ membership.ref }}
            </div>

            <div class="dt-table__data-separator non-mobile-only"></div>

            <div>
                <i class="fa-solid fa-fw fa-calendar"></i>
                {{ l('MANAGE_ACCOUNT.EXPIRES') }}
                {{ dateFilter(membership.maxSubscriptionExpiryDate) }}
            </div>

            <div class="dt-table__data-separator"></div>

            <div v-if="willAutoRenew === false">
                <i class="text-danger fa-solid fa-fw fa-arrow-rotate-right"></i>
                {{ l('MANAGE_ACCOUNT.AUTO_RENEW_OFF_LABEL') }}
            </div>
            <div v-else-if="willAutoRenew === true">
                <i class="text-success fa-solid fa-fw fa-arrow-rotate-right"></i>
                {{ l('MANAGE_ACCOUNT.AUTO_RENEW_ON_LABEL') }}
            </div>
        </td>

        <!-- Plan name and members cell -->
        <td>
            <div class="membership-status detail"
                 :class="{'animated shake' : membership.highlightStatus == true}">
                <i class="fa-solid fa-fw fa-circle"
                   :class="{
                       'text-warning': isPostponed,
                       'text-success': isActive,
                       'text-danger': !isActive
                   }"></i>
                {{ membership.currentPlan.name }}
            </div>

            <div class="dt-result-member-names first"
                 :class="{'inactive': !isActive}">
                <ul>
                    <li v-for="member in visibleMembers"
                        :key="member.ID">
                        <i class="fa-solid fa-user"></i>
                        <ContactName :contact="member.contact" />
                    </li>
                </ul>
            </div>
        </td>

        <!-- Button cell -->
        <td class="dt-table__cell--last dt-table__btn-cell desktop-only">
            <button class="btn btn-lg btn-info dt-table__view-btn mr-0"
                    @click.stop="$emit('edit', membership)">
                <i class="fa-solid fa-pencil"></i>
            </button>

            <div v-if="selectable"
                 class="toggle-container"
                 @click.stop="$emit('select', membership)">
                <Toggle :name="`membership-${membership.ID}-selected`"
                        size="sm"
                        :disabled="!isActive"
                        :borderless="true"
                        :model-value="isSelected"
                        :read-only="true"
                        :label="toggleLabel" />
            </div>
        </td>
    </tr>
</template>

<script>
import ContactName from '@/epos/vue/components/Customers/ContactName';
import Toggle from '@/vue/components/Forms/Toggle';
import { sortByName } from '@/functions/arraySorting';

export default {
    components: {
        Toggle,
        ContactName,
    },

    props: {
        /**
         * @type {Membership}
         */
        membership: {
            type: Object,
            required: true,
        },

        selectable: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['select', 'edit', 'click'],

    computed: {
        isSelected() {
            return this.$store.state.accountDetails.selectedMemberships.includes(this.membership);
        },

        /**
         * @returns {Member[]}
         */
        visibleMembers() {
            const members = this.membership.getMembers().filter((member) => member.contact?.getFullName());
            sortByName(members);
            return members;
        },

        isPostponed() {
            return this.membership.isPostponed();
        },

        isActive() {
            return this.membership.isActive();
        },

        willAutoRenew() {
            return this.membership.willAutoRenew();
        },

        toggleLabel() {
            if (this.isSelected) {
                return 'MANAGE_ACCOUNT_MEMBERSHIPS.SELECTED';
            }

            if (!this.isActive) {
                return 'MANAGE_ACCOUNT_MEMBERSHIPS.INACTIVE';
            }

            return 'MANAGE_ACCOUNT_MEMBERSHIPS.SELECT';
        },
    },
};
</script>

<style lang="less">
.membership-list-membership {
    .toggle {
        label {
            font-weight: normal;
        }
    }

    &.inactive {
        .toggle-container {
            opacity: .5;
        }
    }
}
</style>
